import { Box, styled } from "@mui/material";
import { ReactComponent as LogoIcon } from '../assets/images/logo.svg';
import { useAuth } from "../contexts/AuthContext";
import { userLogin } from "../services/AuthService";
import { getUser } from "../services/UserService";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import ForgottenPassword from "../componenets/ForgottenPassword";
import LoginForm from "../componenets/LoginForm";
import { layout } from "../theme";

const PageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "flex-start",
  // sit below the env banner (0px on prod) so it pushes the login down
  // instead of the banner overlaying the photo.
  marginTop: `${layout.envBannerHeight}px`,
  minHeight: `calc(100dvh - ${layout.envBannerHeight}px)`,
  width: "100%",
  overflow: "hidden",
  backgroundColor: "#070B14",
  [theme.breakpoints.down("md")]: {
    // mobile: stack photo on top, form below — a real column, no overlap
    flexDirection: "column",
    height: `calc(100dvh - ${layout.envBannerHeight}px)`,
    minHeight: 0,
  },
}));

/*
 * The source photo is a tall portrait (1522x3200, ~1:2.1).
 * On desktop we give it its own flexible column so it keeps a
 * natural orientation instead of being zoomed to fill a wide
 * viewport. On mobile (tall/narrow) it goes full-bleed behind
 * the form, which matches its aspect ratio nicely.
 */
const PhotoZone = styled(Box)(({ theme }) => ({
  position: "relative",
  // narrow + tall column so the portrait figure shows almost in full with cover
  flex: "0 0 clamp(360px, 34vw, 560px)",
  minHeight: "100dvh",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    // on mobile we drop the photo entirely and let the form own the screen
    display: "none",
  },
}));

const BackgroundImage = styled('img')(({ theme }) => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  // narrow tall column ≈ portrait ratio, so very little is cropped
  objectPosition: "center top",
  filter: "grayscale(100%) contrast(1.05)",
  zIndex: 0,
  [theme.breakpoints.down("md")]: {
    objectPosition: "center top",
  },
}));

// Cinematic overlay over the photo zone: darken + a brand-green wash on the form-facing edge.
const Overlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 1,
  pointerEvents: "none",
  // on desktop the panel sits to the start (right) side, so fade toward that edge
  background:
    "linear-gradient(270deg, rgba(7,11,20,0.72) 0%, rgba(7,11,20,0.18) 45%, rgba(7,11,20,0.08) 100%)",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(140% 70% at 100% 50%, rgba(1,187,167,0.16) 0%, rgba(1,187,167,0) 60%)",
  },
  [theme.breakpoints.down("md")]: {
    // fade the bottom of the photo into the form's dark background
    background:
      "linear-gradient(180deg, rgba(7,11,20,0.25) 0%, rgba(7,11,20,0.35) 55%, rgba(9,13,21,1) 100%)",
    "&::after": { display: "none" },
  },
}));

// Dark form column, fixed width on desktop so the photo keeps the rest.
const PanelSection = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  flex: "1 1 auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100dvh",
  width: "100%",
  boxSizing: "border-box",
  overflowX: "hidden",
  padding: "48px 56px",
  background:
    "linear-gradient(180deg, #0D1320 0%, #090D15 100%)",
  borderInlineStart: "1px solid rgba(255,255,255,0.06)",
  boxShadow: "-24px 0 60px rgba(0,0,0,0.45)",
  [theme.breakpoints.down("md")]: {
    // size to the form's own content so the button is always fully visible;
    // the photo above absorbs the remaining height
    flex: "1 1 auto",
    width: "100%",
    minHeight: 0,
    justifyContent: "center",
    alignItems: "stretch",
    padding: "24px 22px max(24px, env(safe-area-inset-bottom))",
    background: "#090D15",
    borderInlineStart: "none",
    boxShadow: "0 -24px 60px rgba(0,0,0,0.5)",
  },
}));

const PanelInner = styled(Box)({
  width: "100%",
  maxWidth: "400px",
  minWidth: 0,
  boxSizing: "border-box",
  marginInline: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  animation: "loginFadeIn 0.55s ease both",
  "@keyframes loginFadeIn": {
    from: { opacity: 0, transform: "translateY(14px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
});

const Brand = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "36px",
  "& svg": {
    height: "44px",
    width: "auto",
  },
  [theme.breakpoints.down("md")]: {
    justifyContent: "center",
    marginBottom: "16px",
    "& svg": { height: "34px" },
  },
}));

const BrandMark = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "56px",
  height: "56px",
  borderRadius: "16px",
  background:
    "linear-gradient(135deg, rgba(1,187,167,0.18) 0%, rgba(1,187,167,0.04) 100%)",
  border: "1px solid rgba(1,187,167,0.25)",
  "& svg": { height: "30px" },
});

const Login = () => {
  const [forgottenPassword, setForgottenPassword] = useState(false);
  const { login, currentUser } = useAuth();
  const [failedLogin, setFailedLogin] = useState(false);

  // Paint the page chrome dark while the login screen is mounted so any
  // overscroll/bounce shows the dark theme instead of the app's white body.
  useEffect(() => {
    const prevBody = document.body.style.backgroundColor;
    const prevHtml = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = "#070B14";
    document.documentElement.style.backgroundColor = "#070B14";
    return () => {
      document.body.style.backgroundColor = prevBody;
      document.documentElement.style.backgroundColor = prevHtml;
    };
  }, []);

  const openForgottenPassword = () => {
    setForgottenPassword(true);
  };

  const closeForgottenPassword = () => {
    setForgottenPassword(false);
  };

  const handleRestLogin = (event) => {
    event.preventDefault();
    const { email, password } = document.forms[0];
    userLogin(email.value, password.value)
      .then(res => {
        const token = res.data.body;
        getUser(token)
          .then(user => {
            login(user.data.body, token);
          }).catch(() => setFailedLogin(true));
      }).catch(() => {
        setFailedLogin(true);
      });
  };

  return (
    <>
      <ForgottenPassword closeForgottenPassword={closeForgottenPassword} forgottenPassword={forgottenPassword} />
      {currentUser != null && <Navigate to="/" replace />}
      <PageContainer>
        <PhotoZone>
          <BackgroundImage src="login-image.webp" alt="" aria-hidden="true" />
          <Overlay />
        </PhotoZone>
        <PanelSection>
          <PanelInner>
            <Brand>
              <BrandMark>
                <LogoIcon />
              </BrandMark>
            </Brand>
            <LoginForm
              openForgottenPassword={openForgottenPassword}
              handleRestLogin={handleRestLogin}
              failedLogin={failedLogin}
            />
          </PanelInner>
        </PanelSection>
      </PageContainer>
    </>
  );
};

export default Login;
